import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";

import GoBackButton from "components/project/GoBackButton";
import AdminFilterBar from "components/admin/AdminFilterBar";
import {
  getAdminFileDownloadUrl,
  getAdminFilesList,
} from "lib/api/adminFiles";
import { downloadAdminFile } from "lib/utils/adminFileDownload";

const DEFAULT_QUERY = {
  keyword: "",
  searchField: "all",
  statusFilter: "all",
  typeFilter: "all",
  facilityFilter: "all",
  sortKey: "registeredAt",
  sortDir: "DESC",
  page: 1,
  limit: 20,
  startDate: null,
  endDate: null,
};

const SCROLL_LOAD_THRESHOLD = 320;

const QUERY_KEYS = [
  "keyword",
  "searchField",
  "statusFilter",
  "typeFilter",
  "facilityFilter",
  "sortKey",
  "sortDir",
  "page",
  "limit",
  "startDate",
  "endDate",
];

const FILTER_QUERY_KEYS = QUERY_KEYS.filter((key) => key !== "page");

const STATUS_CLASS = {
  normal: "ok",
  damaged: "damaged",
  pending: "pending",
};

const TYPE_CLASS = {
  image: "image",
  video: "video",
};

const FACILITY_CLASS = {
  electric: "elec",
  gas: "gas",
  machine: "machine",
};

function getFacilityClass(value) {
  const text = String(value ?? "").toLowerCase();
  if (text.includes("전기") || text.includes("electric")) return FACILITY_CLASS.electric;
  if (text.includes("가스") || text.includes("gas")) return FACILITY_CLASS.gas;
  if (text.includes("기계") || text.includes("machine") || text.includes("mechan")) {
    return FACILITY_CLASS.machine;
  }
  return FACILITY_CLASS.machine;
}

function getComparableQueryValue(value) {
  if (value instanceof Date) return value.getTime();
  if (value == null) return "";
  return value;
}

function areQueriesEqual(left, right, keys = QUERY_KEYS) {
  return keys.every(
    (key) =>
      getComparableQueryValue(left?.[key]) === getComparableQueryValue(right?.[key]),
  );
}

function getWindowScrollMetrics() {
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
  const clientHeight = window.innerHeight || doc.clientHeight || 0;
  const scrollHeight = Math.max(
    doc.scrollHeight || 0,
    body.scrollHeight || 0,
    doc.offsetHeight || 0,
    body.offsetHeight || 0,
  );

  return {
    scrollTop,
    clientHeight,
    scrollHeight,
    distanceToBottom: scrollHeight - scrollTop - clientHeight,
  };
}

function getElementScrollMetrics(element) {
  return {
    scrollTop: element.scrollTop || 0,
    clientHeight: element.clientHeight || 0,
    scrollHeight: element.scrollHeight || 0,
    distanceToBottom:
      (element.scrollHeight || 0) - (element.scrollTop || 0) - (element.clientHeight || 0),
  };
}

export default function AdminFilesPage() {
  const { t } = useTranslation();
  const contentRootRef = useRef(null);
  const tableWrapRef = useRef(null);
  const isLoadingRef = useRef(false);
  const lastScrollTopMapRef = useRef(new Map());
  const [keyword, setKeyword] = useState(DEFAULT_QUERY.keyword);
  const [searchField, setSearchField] = useState(DEFAULT_QUERY.searchField);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_QUERY.statusFilter);
  const [typeFilter, setTypeFilter] = useState(DEFAULT_QUERY.typeFilter);
  const [facilityFilter, setFacilityFilter] = useState(DEFAULT_QUERY.facilityFilter);
  const [sortKey, setSortKey] = useState(DEFAULT_QUERY.sortKey);
  const [sortDir, setSortDir] = useState(DEFAULT_QUERY.sortDir);
  const [limit, setLimit] = useState(DEFAULT_QUERY.limit);
  const [startDate, setStartDate] = useState(DEFAULT_QUERY.startDate);
  const [endDate, setEndDate] = useState(DEFAULT_QUERY.endDate);

  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: DEFAULT_QUERY.limit,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadMoreMessage, setLoadMoreMessage] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const searchFieldOptions = useMemo(
    () => [
      { value: "all", label: t("adminFiles.filters.searchFields.all") },
      { value: "owner", label: t("adminFiles.filters.searchFields.owner") },
      { value: "filename", label: t("adminFiles.filters.searchFields.filename") },
      { value: "project", label: t("adminFiles.filters.searchFields.project") },
      { value: "location", label: t("adminFiles.filters.searchFields.location") },
      { value: "facility", label: t("adminFiles.filters.searchFields.facility") },
    ],
    [t],
  );

  const keywordPlaceholder = useMemo(() => {
    const keyByField = {
      owner: "owner",
      filename: "filename",
      project: "project",
      location: "location",
      facility: "facility",
      all: "all",
    };
    return t(`adminFiles.filters.placeholders.${keyByField[searchField] || "all"}`);
  }, [searchField, t]);

  const extraFilters = useMemo(
    () => [
      {
        key: "statusFilter",
        value: statusFilter,
        onChange: setStatusFilter,
        options: [
          { value: "all", label: t("adminFiles.filters.status.all") },
          { value: "normal", label: t("adminFiles.filters.status.normal") },
          { value: "damaged", label: t("adminFiles.filters.status.damaged") },
          { value: "pending", label: t("adminFiles.filters.status.pending") },
        ],
      },
      {
        key: "typeFilter",
        value: typeFilter,
        onChange: setTypeFilter,
        options: [
          { value: "all", label: t("adminFiles.filters.type.all") },
          { value: "image", label: t("adminFiles.filters.type.image") },
          { value: "video", label: t("adminFiles.filters.type.video") },
        ],
      },
      {
        key: "facilityFilter",
        value: facilityFilter,
        onChange: setFacilityFilter,
        options: [
          { value: "all", label: t("adminFiles.filters.facility.all") },
          { value: "전기설비", label: t("adminFiles.filters.facility.electric") },
          { value: "가스설비", label: t("adminFiles.filters.facility.gas") },
          { value: "기계설비", label: t("adminFiles.filters.facility.machine") },
        ],
      },
      {
        key: "sortKey",
        value: sortKey,
        onChange: setSortKey,
        options: [
          { value: "registeredAt", label: t("adminFiles.filters.sort.registeredAt") },
          { value: "filename", label: t("adminFiles.filters.sort.filename") },
          { value: "owner", label: t("adminFiles.filters.sort.owner") },
          { value: "projectName", label: t("adminFiles.filters.sort.project") },
        ],
      },
      {
        key: "sortDir",
        value: sortDir,
        onChange: setSortDir,
        options: [
          { value: "DESC", label: t("adminFiles.filters.sort.desc") },
          { value: "ASC", label: t("adminFiles.filters.sort.asc") },
        ],
      },
      {
        key: "limit",
        value: String(limit),
        onChange: (value) => setLimit(Number(value)),
        options: [
          { value: "10", label: t("adminFiles.pagination.limit", { count: 10 }) },
          { value: "20", label: t("adminFiles.pagination.limit", { count: 20 }) },
          { value: "50", label: t("adminFiles.pagination.limit", { count: 50 }) },
        ],
      },
    ],
    [facilityFilter, limit, sortDir, sortKey, statusFilter, t, typeFilter],
  );

  const currentFilterQuery = useMemo(
    () => ({
      keyword,
      searchField,
      statusFilter,
      typeFilter,
      facilityFilter,
      sortKey,
      sortDir,
      limit,
      startDate,
      endDate,
      page: 1,
    }),
    [
      keyword,
      searchField,
      statusFilter,
      typeFilter,
      facilityFilter,
      sortKey,
      sortDir,
      limit,
      startDate,
      endDate,
    ],
  );

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setQuery((previous) =>
        areQueriesEqual(previous, currentFilterQuery) ? previous : currentFilterQuery,
      );
    }, 250);

    return () => window.clearTimeout(timerId);
  }, [currentFilterQuery]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      isLoadingRef.current = true;
      setLoadMoreMessage("");
      if (query.page === 1) {
        lastScrollTopMapRef.current = new Map();
        contentRootRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        tableWrapRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        setErrorMessage("");
        setRows([]);
        setTotalCount(0);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          limit: query.limit,
        });
      }
      try {
        const result = await getAdminFilesList(query);
        if (cancelled) return;

        if (!result?.success) {
          if (query.page === 1) {
            setRows([]);
            setTotalCount(0);
            setPagination({
              currentPage: 1,
              totalPages: 1,
              limit: query.limit,
            });
            setErrorMessage(result?.message || t("adminFiles.viewerError"));
          } else {
            setLoadMoreMessage(result?.message || t("adminFiles.viewerError"));
          }
          return;
        }

        const nextRows = Array.isArray(result.rows) ? result.rows : [];
        setRows((previousRows) => {
          if (query.page === 1) return nextRows;

          const seenIds = new Set(
            previousRows.map((row) => row?.upload_id || row?.id).filter(Boolean),
          );
          const uniqueNextRows = nextRows.filter((row) => {
            const rowId = row?.upload_id || row?.id;
            if (!rowId) return true;
            if (seenIds.has(rowId)) return false;
            seenIds.add(rowId);
            return true;
          });

          return [...previousRows, ...uniqueNextRows];
        });
        setTotalCount(result.totalCount || 0);
        setPagination(
          result.pagination || {
            currentPage: query.page,
            totalPages: query.page,
            limit: query.limit,
          },
        );
      } catch (error) {
        if (cancelled) return;
        if (query.page === 1) {
          setRows([]);
          setTotalCount(0);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            limit: query.limit,
          });
          setErrorMessage(error?.message || t("adminFiles.viewerError"));
        } else {
          setLoadMoreMessage(error?.message || t("adminFiles.viewerError"));
        }
      } finally {
        if (!cancelled) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [query, t]);

  const submitSearch = (event) => {
    event.preventDefault();
    setQuery((previous) =>
      areQueriesEqual(previous, currentFilterQuery) ? previous : currentFilterQuery,
    );
  };

  const clearAllFilters = () => {
    setKeyword(DEFAULT_QUERY.keyword);
    setSearchField(DEFAULT_QUERY.searchField);
    setStatusFilter(DEFAULT_QUERY.statusFilter);
    setTypeFilter(DEFAULT_QUERY.typeFilter);
    setFacilityFilter(DEFAULT_QUERY.facilityFilter);
    setSortKey(DEFAULT_QUERY.sortKey);
    setSortDir(DEFAULT_QUERY.sortDir);
    setLimit(DEFAULT_QUERY.limit);
    setStartDate(DEFAULT_QUERY.startDate);
    setEndDate(DEFAULT_QUERY.endDate);
    setQuery(DEFAULT_QUERY);
  };

  const handleDownloadClick = async (row) => {
    const fileId = row?.upload_id || row?.id;
    if (!fileId) return;

    setDownloadingId(fileId);
    setErrorMessage("");

    try {
      const result = await downloadAdminFile(
        getAdminFileDownloadUrl(fileId),
        row.filename || "",
      );

      if (!result?.success) {
        setErrorMessage(result?.message || t("adminFiles.downloadError"));
      }
    } catch (error) {
      setErrorMessage(error?.message || t("adminFiles.downloadError"));
    } finally {
      setDownloadingId(null);
    }
  };

  const currentPage = Number(pagination.currentPage || query.page || 1);
  const currentLimit = Number(pagination.limit || query.limit || DEFAULT_QUERY.limit);
  const fallbackTotalPages =
    totalCount > 0 ? Math.max(1, Math.ceil(totalCount / currentLimit)) : 1;
  const totalPages = Number(pagination.totalPages || fallbackTotalPages || 1);
  const hasMore = currentPage < totalPages || (totalCount > 0 && rows.length < totalCount);
  const isInitialLoading = isLoading && query.page === 1 && rows.length === 0;

  const loadNextPage = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;

    isLoadingRef.current = true;
    setQuery((previous) => {
      if (!areQueriesEqual(previous, currentFilterQuery, FILTER_QUERY_KEYS)) {
        return currentFilterQuery;
      }

      const activePage = Number(previous.page || pagination.currentPage || 1);
      const activeLimit = Number(pagination.limit || previous.limit || DEFAULT_QUERY.limit);
      const fallbackTotalPages =
        totalCount > 0 ? Math.max(1, Math.ceil(totalCount / activeLimit)) : 1;
      const totalPages = Number(pagination.totalPages || 1);
      const maxPage = Math.max(totalPages, fallbackTotalPages);

      if (
        activePage >= maxPage ||
        (totalCount > 0 && rows.length >= totalCount)
      ) {
        isLoadingRef.current = false;
        return previous;
      }

      return {
        ...previous,
        page: activePage + 1,
      };
    });
  }, [
    currentFilterQuery,
    hasMore,
    pagination.currentPage,
    pagination.limit,
    pagination.totalPages,
    rows.length,
    totalCount,
  ]);

  const maybeLoadNextPageFromScroll = useCallback(
    (source) => {
      if (!source || !hasMore || isLoadingRef.current) return;

      const metrics =
        source === window
          ? getWindowScrollMetrics()
          : getElementScrollMetrics(source);
      const previousTop = lastScrollTopMapRef.current.get(source) ?? 0;
      lastScrollTopMapRef.current.set(source, metrics.scrollTop);

      const canScroll = metrics.scrollHeight > metrics.clientHeight + 1;
      const isScrollingDown = metrics.scrollTop > previousTop;

      if (!canScroll || !isScrollingDown || metrics.scrollTop <= 0) return;

      if (metrics.distanceToBottom <= SCROLL_LOAD_THRESHOLD) {
        loadNextPage();
      }
    },
    [hasMore, loadNextPage],
  );

  const handleContentScroll = useCallback(
    (event) => {
      maybeLoadNextPageFromScroll(event.currentTarget);
    },
    [maybeLoadNextPageFromScroll],
  );

  useEffect(() => {
    if (!hasMore) return undefined;

    const root = contentRootRef.current;
    const tableWrap = tableWrapRef.current;
    const handleRootScroll = () => maybeLoadNextPageFromScroll(root);
    const handleTableWrapScroll = () => maybeLoadNextPageFromScroll(tableWrap);
    const handleWindowScroll = () => maybeLoadNextPageFromScroll(window);

    root?.addEventListener("scroll", handleRootScroll, { passive: true });
    tableWrap?.addEventListener("scroll", handleTableWrapScroll, { passive: true });
    window.addEventListener("scroll", handleWindowScroll, { passive: true });

    return () => {
      root?.removeEventListener("scroll", handleRootScroll);
      tableWrap?.removeEventListener("scroll", handleTableWrapScroll);
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, [hasMore, maybeLoadNextPageFromScroll, rows.length]);

  return (
    <section
      className="content admin adminFilesPage"
      ref={contentRootRef}
      onScroll={handleContentScroll}
    >
      <div className="helpTitleRow">
        <GoBackButton index />
        <h2>{t("adminFiles.title")}</h2>
      </div>

      <AdminFilterBar
        keyword={keyword}
        onChangeKeyword={setKeyword}
        keywordPlaceholder={keywordPlaceholder}
        searchField={searchField}
        onChangeSearchField={setSearchField}
        searchFieldOptions={searchFieldOptions}
        extraFilters={extraFilters}
        startDate={startDate}
        endDate={endDate}
        onChangeDateRange={(start, end) => {
          setStartDate(start);
          setEndDate(end);
        }}
        onSubmit={submitSearch}
        onResetAll={clearAllFilters}
        isLoading={isLoading}
        resultCount={totalCount}
        resultUnit={t("adminFiles.resultUnit")}
      />

      <div
        className="card detailTableWrap fileTableCard"
        ref={tableWrapRef}
        onScroll={handleContentScroll}
      >
        <table className="detailTable fileKpiTable">
          <thead>
            <tr>
              <th>{t("adminFiles.columns.registeredAt")}</th>
              <th>{t("adminFiles.columns.owner")}</th>
              <th>{t("adminFiles.columns.filename")}</th>
              <th>{t("adminFiles.columns.projectName")}</th>
              <th>{t("adminFiles.columns.fileStatus")}</th>
              <th>{t("adminFiles.columns.fileType")}</th>
              <th>{t("adminFiles.columns.facilityType")}</th>
              <th>{t("adminFiles.columns.location")}</th>
              <th>{t("adminFiles.columns.facilityName")}</th>
              <th>{t("adminFiles.columns.download")}</th>
            </tr>
          </thead>
          <tbody>
            {errorMessage ? (
              <tr className="emptyRow">
                <td colSpan={10} className="emptyCell">
                  {errorMessage}
                </td>
              </tr>
            ) : isInitialLoading ? (
              <tr className="emptyRow">
                <td colSpan={10} className="emptyCell">
                  {t("adminFiles.loadingMessage")}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr className="emptyRow">
                <td colSpan={10} className="emptyCell">
                  {t("adminFiles.noResults")}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const fileId = row.upload_id || row.id;

                return (
                  <tr key={fileId}>
                    <td>{row.registeredAt}</td>
                    <td>{row.owner}</td>
                    <td title={row.filename}>{row.filename}</td>
                    <td title={row.projectName}>{row.projectName}</td>
                    <td>
                      <span
                        className={`statusTag status-${
                          STATUS_CLASS[row.fileStatusCode] ?? "ok"
                        }`}
                      >
                        {t(`adminFiles.status.${row.fileStatusCode}`, {
                          defaultValue: row.fileStatus,
                        })}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`typeTag type-${
                          TYPE_CLASS[row.fileTypeCode] ?? "doc"
                        }`}
                      >
                        {t(`adminFiles.fileType.${row.fileTypeCode || "other"}`, {
                          defaultValue: row.fileType || "-",
                        })}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`facilityTag facility-${getFacilityClass(
                          row.facilityType,
                        )}`}
                      >
                        {row.facilityType || "-"}
                      </span>
                    </td>
                    <td title={row.location}>{row.location || "-"}</td>
                    <td title={row.facilityName}>{row.facilityName || "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="downloadBtn"
                        onClick={() => handleDownloadClick(row)}
                        disabled={downloadingId === fileId}
                        title={t("adminFiles.originalDownload")}
                      >
                        <Download size={14} aria-hidden="true" />
                        {downloadingId === fileId
                          ? t("adminFiles.actions.downloading")
                          : t("adminFiles.actions.download")}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="adminFilePagination">
        {isLoading && rows.length > 0 ? (
          <span>{t("adminFiles.loadingMessage")}</span>
        ) : loadMoreMessage ? (
          <span>{loadMoreMessage}</span>
        ) : hasMore ? (
          <span>
            {t("adminFiles.pagination.scrollGuide", {
              defaultValue: "아래로 스크롤하면 더 불러옵니다.",
            })}
          </span>
        ) : rows.length > 0 ? (
          <span>
            {t("adminFiles.pagination.end", {
              defaultValue: "마지막 목록입니다.",
            })}
          </span>
        ) : null}
      </div>
    </section>
  );
}
